import { IPromise } from 'angular'
import React, { useState, useEffect } from 'react'
import { ApiCustomer } from '@vend/types'
import { reactular } from '@vendhq/reactular'
import {
  CONTACT_OPTIONS,
  CustomerInfo,
  PartialCustomerGroup,
} from '../common/types'

import {
  useRegisterManager,
  useVdFeatureManager,
} from '../../../../ng-compat/hooks'
import { customerGroupsStore } from '../../../../factories/database/customer-groups-store'
import {
  ALL_CUSTOMERS_GROUP_ID,
  detectCustomerFieldFromQuery,
} from '../../../../functions/customer'
import { $q } from '../../../../ng-compat/imports'
import {
  selectFulfilmentDetails,
  selectLineItemsTotalQuantity,
  selectSaleCustomer,
  selectSaleFulfilmentDetail,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../../../state/hooks'
import { FulfilmentType } from '../../../../models/fulfilment'
import { Step1 } from './Steps/Step1/Step1'
import { Step2 } from './Steps/Step2/Step2'
import { Step3 } from './Steps/Step3/Step3'
import { FulfillmentOutlet, STEPS } from './Steps/types'
import { Step2Add } from './Steps/Step2/Step2Add'

interface Props {
  close: (val?: IPromise<any>) => void
  outlets: FulfillmentOutlet[]
}

const contactUpdated = (
  customer: ApiCustomer | null,
  originalCustomer: ApiCustomer | null
): boolean => {
  if (!customer || !originalCustomer) {
    return false
  }
  return Object.values(CONTACT_OPTIONS).some(option => {
    const value = customer[option] && customer[option]!.trim()
    const preValue =
      originalCustomer[option] && originalCustomer[option]!.trim()
    return value !== preValue
  })
}

export const MarkAsUnfulfilledModal: React.FC<Props> = ({ close, outlets }) => {
  const customer = useAppSelector(selectSaleCustomer)
  const fulfilmentDetail = useAppSelector(selectSaleFulfilmentDetail)
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [type, setType] = useState<FulfilmentType>(() => {
    if (
      !fulfilmentDetail?.type ||
      fulfilmentDetail.type === FulfilmentType.UNSPECIFIED
    ) {
      return FulfilmentType.DELIVERY
    }

    return fulfilmentDetail.type
  })
  const [newCustomer, setNewCustomer] = useState<CustomerInfo | null>(null)
  const [selectedCustomer, setSelectedCustomer] = useState<ApiCustomer | null>(
    null
  )
  const [originalCustomer, setOriginalCustomer] = useState<ApiCustomer | null>(
    null
  )
  const [group, setGroup] = useState<PartialCustomerGroup | undefined>(
    undefined
  )

  const totalQuantity = useAppSelector(selectLineItemsTotalQuantity)

  const vdFeatureManager = useVdFeatureManager()
  const shouldShowGroup = vdFeatureManager.get('customer_groups')

  if (
    shouldShowGroup &&
    selectedCustomer &&
    selectedCustomer.customer_group_id &&
    !group
  ) {
    customerGroupsStore
      .get(selectedCustomer.customer_group_id)
      .then(groupEntity => {
        if (groupEntity) {
          setGroup({
            id: groupEntity.id,
            name: groupEntity.name,
          })
        }
      })
  }

  if (newCustomer && !group) {
    customerGroupsStore.all().then(groups => {
      if (groups) {
        const defaultGroup = groups.find(
          item => item.group_id === ALL_CUSTOMERS_GROUP_ID
        )
        defaultGroup &&
          setGroup({
            id: defaultGroup.id,
            name: defaultGroup.name,
          })
      }
    })
  }

  const [fulfillmentOutletID, setFulfillmentOutletID] = useState<string | null>(
    null
  )
  const fulfilmentDetails = useAppSelector(selectFulfilmentDetails)
  const defaultNote = fulfilmentDetails[0]?.note || ''
  const [note, setNote] = useState<string>(defaultNote)

  const registerManager = useRegisterManager()
  const currentOutlet = registerManager.getActiveOutlet().id

  useEffect(() => {
    if (customer && customer.customer_code !== 'WALKIN') {
      setOriginalCustomer(customer)
      setSelectedCustomer(customer)
    }
  }, [customer, setSelectedCustomer])

  const handleTypeChange = (type: FulfilmentType) => {
    setType(type)
  }

  const handleStepChange = (step: STEPS) => {
    setCurrentStep(step)
  }

  const handleOutletSelection = (outlet: string) => {
    setFulfillmentOutletID(outlet)
  }

  const closeAngularModal = (hasMarked?: boolean) => {
    close($q.resolve(hasMarked))
  }

  const handleAddCustomer = () => {
    const inputNode: HTMLInputElement | null = document.querySelector(
      '.modal-mark-as-unfulfilled .vd-autocomplete-input'
    )
    const newName = inputNode?.value

    if (newName) {
      const customer = detectCustomerFieldFromQuery(newName)
      setNewCustomer(prevState => ({
        ...prevState,
        ...customer,
      }))
      setSelectedCustomer(null)
      setOriginalCustomer(null)
      handleStepChange(STEPS.ADD)
    }
  }

  const handleSelectCustomer = (customer: ApiCustomer | null) => {
    setGroup(undefined)
    if (customer) {
      setSelectedCustomer(customer)
      setOriginalCustomer(customer)
      setNewCustomer(null)
    } else {
      setSelectedCustomer(null)
    }
  }

  const handleNewContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    const { value, name } = e.target
    if (name) {
      setNewCustomer(prevState => ({
        ...prevState,
        [name]: value,
      }))
    }
  }

  const handleSelectedContactChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault()
    const { value, name } = e.target
    if (name) {
      setSelectedCustomer((prev: $TSFixMe) => {
        return {
          ...prev,
          [name]: value,
        }
      })
    }
  }

  const getStepComponent = () => {
    switch (currentStep) {
      case STEPS.METHOD:
        return (
          <Step1
            onStepChange={handleStepChange}
            onTypeChange={handleTypeChange}
            onOutletChange={handleOutletSelection}
            onClose={closeAngularModal}
            type={type}
            outlets={outlets}
            currentOutlet={fulfillmentOutletID || currentOutlet}
            totalQuantity={totalQuantity}
          />
        )
      case STEPS.CUSTOMER:
        return (
          <Step2
            onStepChange={handleStepChange}
            onClose={closeAngularModal}
            onAdd={handleAddCustomer}
            onSelect={handleSelectCustomer}
            onContactChange={handleSelectedContactChange}
            newCustomer={newCustomer}
            selectedCustomer={selectedCustomer}
            group={group}
          />
        )
      case STEPS.ADD:
        return (
          <Step2Add
            onStepChange={handleStepChange}
            onClose={closeAngularModal}
            onChange={handleNewContactChange}
            type={type}
            newCustomer={newCustomer}
          />
        )
      case STEPS.DETAILS:
        return (
          <Step3
            onStepChange={handleStepChange}
            onClose={closeAngularModal}
            handleSaleNoteChange={setNote}
            note={note}
            type={type}
            fulfillmentOutletID={
              currentOutlet === fulfillmentOutletID ? null : fulfillmentOutletID
            }
            newCustomer={newCustomer}
            selectedCustomer={selectedCustomer}
            group={group}
            contactChanged={contactUpdated(selectedCustomer, originalCustomer)}
            totalQuantity={totalQuantity}
          />
        )
      default:
        return null
    }
  }

  return <div className="modal-mark-as-unfulfilled">{getStepComponent()}</div>
}

MarkAsUnfulfilledModal.displayName = 'MarkAsUnfulfilledModal'

export const MarkAsUnfulfilledModalComponent = reactular(
  MarkAsUnfulfilledModal,
  ['close', 'outlets'],
  'reactWrapper'
)
